'use client';

import i18n from "@/lang/i18n";
import { Box, Typography, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useDarkColors } from '@/hooks/useDarkColors';
import { useTranslator } from '@/lang';
import {
  getReactionList,
  type ReactionListItem,
  type ReactionTarget,
  type ReactionType,
} from '@/api/userFeedApi';
import { TEMP_TEXT_BOLD_M, TEMP_TEXT_REGULAR_M } from '@/constants/tempTypo';
import { getAssetUrl } from '@/utils/assetUrl';

export const REACTION_EMOJI: Record<ReactionType, string> = {
  HEART: '❤️',
  LAUGH: '😂',
  WOW: '😮',
  SAD: '😢',
  ANGRY: '😡',
  FIRE: '🔥',
  CLAP: '👏',
};

interface Props {
  feedId: string;
  targetType: ReactionTarget;
  targetId: string;
}

/**
 * 피드/댓글/대댓글에 달린 캐릭터 이모지 반응 리스트 — 본문 전용.
 *
 * Drawer 래퍼는 가지지 않는다. `usePortalStore.openBottomSheet({ content: ... })`로
 * 띄워야 핸들 드래그/배경 탭 닫기 등 표준 바텀시트 UX가 적용된다.
 */
export default function ReactionListSheet({ feedId, targetType, targetId }: Props) {
  const c = useDarkColors();
  const { t, f } = useTranslator();
  const { data, isLoading } = useQuery({
    queryKey: ['reaction-list', feedId, targetType, targetId],
    queryFn: () => getReactionList(feedId, targetType, targetId),
  });
  const items = data?.items ?? [];

  return (
    <Box sx={{ bgcolor: c.bg, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${c.border}`,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: c.fg }}>
          {f(t('my_feed.reactions.title'), [items.length])}
        </Typography>
      </Box>
      {/* data-scroll-container: 포털 열림 중 useBodyScrollLock 전역 touchmove 차단 화이트리스트 */}
      <Box data-scroll-container sx={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography sx={{ ...TEMP_TEXT_REGULAR_M, color: c.fgLow }}>
              {t('my_feed.reactions.empty')}
            </Typography>
          </Box>
        ) : (
          items.map((it: ReactionListItem) => {
            const name = pickName(it.characterName, t);
            const url = it.characterProfileImagePath
              ? getAssetUrl(it.characterProfileImagePath, 'large')
              : null;
            return (
              <Box
                key={it.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderBottom: `1px solid ${c.border}`,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor: c.bgInset,
                    overflow: 'hidden',
                    backgroundImage: url ? `url(${url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: c.fg, flex: 1 }}>
                  {name}
                </Typography>
                <Box sx={{ fontSize: 22 }}>{REACTION_EMOJI[it.reactionType]}</Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}

function pickName(name: any, t: (key: string) => string): string {
  const muse = t('my_feed.author.muse');
  if (!name) return muse;
  if (typeof name === 'string') return name;
  if (typeof name === 'object')
    return name[i18n.language] ?? name.ko ?? name.en ?? Object.values(name)[0] ?? muse;
  return muse;
}
